import { useLanguage, useMessageActionContext } from '@/app/providers';
import { Checkbox, CustomButton } from '@/components';
import MessageActionDropdown from '@/features/chats/ui/Conversation/components/MessageActionDropdown';
import { useMessageAction } from '@/features/chats/viewmodels/useMessageAction';
import { formatChatTime } from '@/features/timeformatter/timeStamp';
import { Username, UserProfile } from '@/features/users';
import { getLocalUserId, getMessageSenderId } from '@/shared/utils';
import { isGroupChat } from '@/features/chats/utils/jidIdFormat';
import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';
import './deletedMessageCard.scss';
import { IconBlock } from '@/assets/images';

const SENDER_CLASS = 'sender-chat chat-bubble ct-rowReverse ct-flex ct-aiEnd ct-gap10 ct-padding-lr8-rl16';
const RECEIVER_CLASS = 'receiver-chat chat-bubble ct-flex ct-gap10 ct-padding-lr8-rl16';

const DeletedMessageCard = memo(({ message, activeChatJid }) => {
   const { isSelected, isSelectionActive, handleToggleSelection } = useMessageAction(message, activeChatJid);

   const senderId = getMessageSenderId({ type: 'chats', message });
   const isSender = senderId === getLocalUserId();
   const { t } = useLanguage();
   const { hideAvatar = false, hideActions = false } = useMessageActionContext();
   const isHighlighted = message?.isHighlighted;

   const timestamp = useMemo(
      () => (message.timestamp ? formatChatTime(new Date(Number(message.timestamp)), 'message-card') : ''),
      [message.timestamp],
   );
   const hideAvatarDeleteMessage = isSender || hideAvatar;
   const isChatGroup = useMemo(() => isGroupChat(activeChatJid), [activeChatJid]);
   const showProfile = isChatGroup && !hideAvatarDeleteMessage;

   return (
      <CustomButton
         as="div"
         className={`message-row custom-deleted-card ${isSelectionActive && !hideActions ? 'ct-flex ct-aiCenter selection-active' : ''} ${isSelected && !hideActions ? 'selected' : ''} ${isHighlighted && !hideActions ? 'search-highlight-active' : ''}`}
         onClick={isSelectionActive && !hideActions ? handleToggleSelection : null}
      >
         {isSelectionActive && !hideActions && (
            <div className="selection-checkbox-wrap cts-deleted">
               <Checkbox checked={isSelected} onChange={handleToggleSelection} />
            </div>
         )}
         <div className={`${isSender ? SENDER_CLASS : RECEIVER_CLASS}`}>
            {showProfile && (
               <picture className="chat-avatar cts-deleted">
                  <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
               </picture>
            )}
            <div className={`ct-flex ct-relative ${isSender ? 'ct-flexColEnd' : 'ct-flexCol'} ct-gap10`}>
               <div
                  className={`chat-box ct-flex ct-flexCol ct-gap10 ${message.deleteStatus ? 'custom-deleted-card' : ''}`}
               >
                  {showProfile && <Username userId={senderId} />}
                  <p className="ct-flex ct-aiCenter ct-gap8 font-italic">
                     <IconBlock />{' '}
                     {t(isSender ? 'message.YOU_DELETED_THIS_MESSAGE' : 'message.OTHER_DELETED_THIS_MESSAGE')}
                  </p>
               </div>
               <div className="ct-flexAlign ct-gap10">
                  <span className="time cts-deleted">{timestamp}</span>
               </div>
               {!hideActions && (
                  <MessageActionDropdown
                     className="cts-deleted"
                     message={message}
                     isUser={isSender}
                     activeChatJid={activeChatJid}
                  />
               )}
            </div>
         </div>
      </CustomButton>
   );
});

DeletedMessageCard.displayName = 'DeletedMessageCard';

DeletedMessageCard.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
};

export default DeletedMessageCard;
